import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Input,
  Switch,
  Alert,
  AlertDescription,
} from '@roger/ui-components';
import { AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/useToastContext';
import {
  getGoalsProgressValidationConfig,
  setGoalsProgressValidationConfig,
  P2GValidationConfigs,
} from '../services/rogerDocApi';

interface GoalsProgressConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GoalsProgressConfigModal: React.FC<GoalsProgressConfigModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<P2GValidationConfigs>({
    require_viewing: true,
    require_goals_progress_when_interventions_performed: false,
    require_progress_note_when_score: false,
    require_performance_note: false,
    min_goals_performed: 0,
    min_interventions_performed: 0,
  });

  useEffect(() => {
    if (open) {
      loadConfig();
    }
  }, [open]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await getGoalsProgressValidationConfig();
      setConfig(data);
    } catch (err) {
      console.error('Failed to load config:', err);
      showError(
        'Load Failed',
        'Failed to load configuration. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setGoalsProgressValidationConfig(config);
      success(
        'Configuration Saved',
        'Goals progress settings have been updated.'
      );
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to save config:', err);
      showError(
        'Save Failed',
        'Failed to save configuration. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Goals Progress Tracking Settings</DialogTitle>
          <DialogDescription>
            Configure rules for tracking progress on goals and interventions.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className='py-8 text-center text-muted-foreground'>
            Loading configuration...
          </div>
        ) : (
          <div className='space-y-6 py-4'>
            {/* Toggle Fields */}
            <div className='space-y-1 rounded-lg border divide-y overflow-hidden'>
              <label className='flex items-center gap-2.5 py-2 px-2.5 cursor-pointer hover:bg-muted/40 transition-colors'>
                <Switch
                  checked={
                    config.require_goals_progress_when_interventions_performed
                  }
                  onCheckedChange={checked =>
                    setConfig({
                      ...config,
                      require_goals_progress_when_interventions_performed:
                        Boolean(checked),
                    })
                  }
                />
                <span className='text-sm font-medium leading-tight'>
                  Require a progress score for a goal if any of its
                  interventions are performed
                </span>
              </label>

              <label className='flex items-center gap-2.5 py-2 px-2.5 cursor-pointer hover:bg-muted/40 transition-colors'>
                <Switch
                  checked={config.require_progress_note_when_score}
                  onCheckedChange={checked =>
                    setConfig({
                      ...config,
                      require_progress_note_when_score: Boolean(checked),
                    })
                  }
                />
                <span className='text-sm font-medium leading-tight'>
                  Require progress note for a goal if a score is selected for
                  that goal
                </span>
              </label>

              <label className='flex items-center gap-2.5 py-2 px-2.5 cursor-pointer hover:bg-muted/40 transition-colors'>
                <Switch
                  checked={config.require_performance_note}
                  onCheckedChange={checked =>
                    setConfig({
                      ...config,
                      require_performance_note: Boolean(checked),
                    })
                  }
                />
                <span className='text-sm font-medium leading-tight'>
                  Require performance note for performed interventions
                </span>
              </label>
            </div>

            {/* Number Fields */}
            <div className='space-y-4'>
              <div className='space-y-1'>
                <Label htmlFor='min_goals_performed'>
                  Minimum number of goals with progress scores required to be
                  able to push a visit to the EMR
                </Label>
                <Input
                  id='min_goals_performed'
                  type='number'
                  min={0}
                  value={config.min_goals_performed}
                  onChange={e =>
                    setConfig({
                      ...config,
                      min_goals_performed: parseInt(e.target.value) || 0,
                    })
                  }
                  inputSize='sm'
                  className='w-24'
                />
              </div>

              <div className='space-y-1'>
                <Label htmlFor='min_interventions_performed'>
                  Minimum number of performed interventions required to be able
                  to push a visit to the EMR
                </Label>
                <Input
                  id='min_interventions_performed'
                  type='number'
                  min={0}
                  value={config.min_interventions_performed}
                  onChange={e =>
                    setConfig({
                      ...config,
                      min_interventions_performed:
                        parseInt(e.target.value) || 0,
                    })
                  }
                  inputSize='sm'
                  className='w-24'
                />
              </div>
            </div>

            {/* Info Alert */}
            <Alert className='border-blue-200 bg-blue-50'>
              <AlertCircle className='h-4 w-4 text-blue-600' />
              <AlertDescription className='text-blue-800'>
                These settings apply to all users when they push visits to the
                EMR. Changes take effect immediately.
              </AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={saving}
            size='sm'
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading || saving} size='sm'>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoalsProgressConfigModal;
